/**
 * InoculationStatusBadge Component
 *
 * Displays production batch status with appropriate styling.
 * Part of US-P2-005: Inoculation Tracking & Production Batch Management
 *
 * Status Flow (15 states):
 * INOCULATED → COLONIZING → PINNING → FRUITING → HARVESTING →
 * POST_HARVEST → DISPOSED | CONTAMINATED
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

const STATUS_CONFIG = {
  // Active states
  INOCULATED: {
    label: 'Inoculated',
    variant: 'outline',
    className: 'border-odin-blue text-odin-blue bg-odin-blue/10'
  },
  COLONIZING: {
    label: 'Colonizing',
    variant: 'outline',
    className: 'border-odin-blue text-odin-blue bg-odin-blue/10'
  },
  PINNING: {
    label: 'Pinning',
    variant: 'outline',
    className: 'border-purple-500 text-purple-500 bg-purple-500/10'
  },
  FRUITING: {
    label: 'Fruiting',
    variant: 'outline',
    className: 'border-odin-green text-odin-green bg-odin-green/10'
  },
  HARVESTING: {
    label: 'Harvesting',
    variant: 'outline',
    className: 'border-amber-500 text-amber-500 bg-amber-500/10'
  },
  HARVESTED: {
    label: 'Harvested',
    variant: 'outline',
    className: 'border-emerald-600 text-emerald-600 bg-emerald-600/10'
  },
  // Preparation for next flush
  RESTING: {
    label: 'Resting',
    variant: 'outline',
    className: 'border-slate-400 text-slate-400 bg-slate-400/10'
  },
  SOAKING: {
    label: 'Soaking',
    variant: 'outline',
    className: 'border-cyan-500 text-cyan-500 bg-cyan-500/10'
  },
  FLUSH_2_PINNING: {
    label: 'Flush 2: Pinning',
    variant: 'outline',
    className: 'border-purple-500 text-purple-500 bg-purple-500/10'
  },
  FLUSH_2_FRUITING: {
    label: 'Flush 2: Fruiting',
    variant: 'outline',
    className: 'border-odin-green text-odin-green bg-odin-green/10'
  },
  FLUSH_2_HARVESTING: {
    label: 'Flush 2: Harvesting',
    variant: 'outline',
    className: 'border-amber-500 text-amber-500 bg-amber-500/10'
  },
  FLUSH_2_HARVESTED: {
    label: 'Flush 2: Harvested',
    variant: 'outline',
    className: 'border-emerald-600 text-emerald-600 bg-emerald-600/10'
  },
  // End states
  POST_HARVEST: {
    label: 'Post-Harvest',
    variant: 'outline',
    className: 'border-slate-500 text-slate-500 bg-slate-500/10'
  },
  DISPOSED: {
    label: 'Disposed',
    variant: 'outline',
    className: 'border-content-tertiary text-content-tertiary bg-surface-secondary'
  },
  CONTAMINATED: {
    label: 'Contaminated',
    variant: 'destructive',
    className: 'border-odin-red bg-odin-red text-white'
  }
}

export default function InoculationStatusBadge({ status, className }) {
  const config = STATUS_CONFIG[status] || {
    label: status,
    variant: 'outline',
    className: ''
  }

  return (
    <Badge
      variant={config.variant}
      className={cn(config.className, className)}
    >
      {config.label}
    </Badge>
  )
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status) {
  return STATUS_CONFIG[status]?.label || status
}

/**
 * Get status category for filtering
 */
export function getStatusCategory(status) {
  if (['INOCULATED', 'COLONIZING'].includes(status)) return 'colonizing'
  if (['PINNING', 'FRUITING', 'HARVESTING', 'HARVESTED'].includes(status)) return 'flush-1'
  if (['RESTING', 'SOAKING'].includes(status)) return 'preparing'
  if (['FLUSH_2_PINNING', 'FLUSH_2_FRUITING', 'FLUSH_2_HARVESTING', 'FLUSH_2_HARVESTED'].includes(status)) return 'flush-2'
  if (['POST_HARVEST', 'DISPOSED'].includes(status)) return 'complete'
  if (status === 'CONTAMINATED') return 'contaminated'
  return 'other'
}

/**
 * Check if status is an active production state
 */
export function isActiveStatus(status) {
  return ![
    'POST_HARVEST',
    'DISPOSED',
    'CONTAMINATED'
  ].includes(status)
}
